/**
 * shared/layouts/WizardLayout/WizardDataProvider.tsx
 * Generic wizard data provider for shared wizard state management
 *
 * Provides reusable wizard state management that works with any wizard implementation:
 * - Type-safe generic data handling
 * - Automatic localStorage persistence
 * - Clean update patterns without race conditions
 * - Works with conditional step logic
 */

import { createContext, useState, useEffect, useCallback, ReactNode } from 'react';

// Internal absolute imports
import { db } from '@/lib/db';
import type { OnboardingDraft } from '@/types/transient/OnboardingDraft';
import { isTombstoned } from '@/utils/tombstone';
import { getOrCreateDeviceId } from '@/utils/deviceDetection';
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import { getSyncOperationService } from '@/services/sync/SyncOperationService';
import { markAsInstrumentedWriteContext } from '@/services/sync/SyncImportContext';
import { buildSetOps, buildDiffOps, buildDeleteOp } from '@/services/sync/OperationBuilder';

export interface WizardDataContextValue<TData = Record<string, unknown>> {
  // State
  data: TData | null;
  loading: boolean;
  error: string | null;

  // Actions
  updateData: (updater: (current: TData) => TData) => void;
  updatePartial: (updates: Partial<TData>) => void;
  resetData: () => void;

  // Utilities
  getData: () => TData | null;
}

// eslint-disable-next-line react-refresh/only-export-components -- WizardDataContext is infrastructure tightly coupled with WizardDataProvider, keeping together improves maintainability
export const WizardDataContext = createContext<WizardDataContextValue<Record<string, unknown>> | null>(null);

interface WizardDataProviderProps<TData = Record<string, unknown>> {
  children: ReactNode;
  initialData: TData;
  storageKey?: string;
  onDataChange?: (data: TData) => void;
}

/**
 * Generic wizard data provider
 * Handles state management, persistence, and updates for any wizard
 */
export const WizardDataProvider = <TData extends Record<string, unknown>>({
  children,
  initialData,
  storageKey,
  onDataChange
}: WizardDataProviderProps<TData>) => {
  const [data, setData] = useState<TData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Save data to IndexedDB if storageKey provided
  const saveToStorage = useCallback(async (dataToSave: TData): Promise<void> => {
    if (!storageKey) return;

    try {
      // Add timestamps for tracking
      const dataWithTimestamps = {
        ...dataToSave,
        id: storageKey,
        createdAt: (dataToSave as Record<string, unknown>)['createdAt'] || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Transaction with sync tables for atomic op logging
      await db.transaction(
        'rw',
        [db.wizard_drafts, db.sync_operations, db.sync_state],
        async () => {
          markAsInstrumentedWriteContext();

          // Read existing draft for diff-based logging
          const existingDraft = await db.wizard_drafts.get(storageKey);

          // Write data
          await db.wizard_drafts.put(dataWithTimestamps as unknown as OnboardingDraft);

          // Generate ops based on whether this is a new entity or update
          const ops = existingDraft
            ? buildDiffOps(
                'wizard_drafts',
                storageKey,
                existingDraft as unknown as Record<string, unknown>,
                dataWithTimestamps as Record<string, unknown>
              )
            : buildSetOps(
                'wizard_drafts',
                storageKey,
                dataWithTimestamps as Record<string, unknown>
              );

          if (ops.length > 0) {
            await getSyncOperationService().logBatch(ops);
          }
        }
      );
    } catch (err) {
      console.error('Failed to save wizard data:', err);
      setError('Failed to save progress');
    }
  }, [storageKey]);

  // Load data from IndexedDB if available
  const loadFromStorage = useCallback(async (): Promise<TData> => {
    if (!storageKey) return initialData;

    try {
      const stored = await db.wizard_drafts.get(storageKey);
      if (stored) {
        // Tombstone check: treat deleted drafts as "no draft"
        if (isTombstoned(stored)) {
          return initialData;
        }

        return { ...initialData, ...stored } as TData;
      }
    } catch (err) {
      console.error('Failed to load wizard data:', err);
      setError('Failed to load progress');
    }

    return initialData;
  }, [storageKey, initialData]);

  // Initialize data on mount
  useEffect(() => {
    const loadData = async (): Promise<void> => {
      const loadedData = await loadFromStorage();
      setData(loadedData);
      setLoading(false);
    };

    loadData();
  }, [loadFromStorage]);

  // Subscribe to cross-tab changes for this specific record
  useEffect(() => {
    if (!storageKey) return;

    // Subscribe to changes from other tabs for this specific record
    const unsubscribe = syncCoordinatorService.subscribeByRecord(
      'wizard_drafts',
      storageKey,
      async () => {
        // CRITICAL: Refetch from Dexie and REPLACE state completely
        // Do NOT merge with current in-memory state (prevents stale keys from surviving)
        const freshDraft = await db.wizard_drafts.get(storageKey);
        if (freshDraft) {
          // Tombstone check: if deleted by another tab, reset to defaults
          if (isTombstoned(freshDraft)) {
            setData(initialData);
            onDataChange?.(initialData);
            return;
          }

          // Full replacement: spread initialData for defaults, then overlay freshDraft
          const replacedData = { ...initialData, ...freshDraft } as TData;
          setData(replacedData);
          onDataChange?.(replacedData);
        }
      }
    );

    // Cleanup: unsubscribe on unmount to prevent memory leaks
    return unsubscribe;
  }, [storageKey, initialData, onDataChange]);

  /**
   * Universal update function with proper state management.
   *
   * Behavior:
   * - Always bumps `lastModified` timestamp at record level
   * - Saves to IndexedDB and logs sync operations
   * - Notifies other tabs of changes
   *
   * @param updater - Function that receives current data and returns updated data
   *
   * @example
   * updateData(current => ({ ...current, account: { ...current.account, accountName: 'New' } }))
   */
  const updateData = useCallback(
    (updater: (current: TData) => TData) => {
      setData(currentData => {
        if (!currentData) return currentData;

        const now = new Date().toISOString();
        const nextData = {
          ...updater(currentData),
          lastModified: now
        } as TData;

        // Save to IndexedDB
        saveToStorage(nextData);

        // Notify other tabs of changes (cross-tab coordination)
        if (storageKey) {
          syncCoordinatorService.notifyChange('wizard_drafts', storageKey);
        }

        // Call onChange callback
        onDataChange?.(nextData);

        setError(null);
        return nextData;
      });
    },
    [saveToStorage, onDataChange, storageKey]
  );

  // Partial update helper
  const updatePartial = useCallback(
    (updates: Partial<TData>) => {
      updateData(current => ({ ...current, ...updates }));
    },
    [updateData]
  );

  // Reset to initial data (writes tombstone for sync-aware deletion)
  const resetData = useCallback(async (): Promise<void> => {
    setData(initialData);
    if (storageKey) {
      try {
        // Write tombstone instead of hard delete for sync propagation
        const now = new Date().toISOString();
        const deviceId = getOrCreateDeviceId();
        const tombstone = {
          id: storageKey,
          // createdAt required by Dexie schema index, not meaningful for tombstone
          createdAt: now,
          updatedAt: now,
          deletedAt: now,
          deletedByDevice: deviceId,
        };

        // Transaction with sync tables for atomic op logging
        await db.transaction(
          'rw',
          [db.wizard_drafts, db.sync_operations, db.sync_state],
          async () => {
            markAsInstrumentedWriteContext();

            await db.wizard_drafts.put(tombstone as unknown as OnboardingDraft);

            // Log delete op for LWW semantics on import
            const deleteOp = buildDeleteOp('wizard_drafts', storageKey);
            await getSyncOperationService().logBatch([deleteOp]);
          }
        );

        // Notify other tabs immediately so they can reset to defaults
        syncCoordinatorService.notifyChange('wizard_drafts', storageKey);
      } catch (err) {
        console.error('Failed to tombstone wizard data:', err);
      }
    }
    setError(null);
  }, [initialData, storageKey]);

  // Getter utility
  const getData = useCallback(() => data, [data]);

  const contextValue: WizardDataContextValue<TData> = {
    // State
    data,
    loading,
    error,

    // Actions
    updateData,
    updatePartial,
    resetData,

    // Utilities
    getData
  };

  return (
    <WizardDataContext.Provider value={contextValue as WizardDataContextValue}>
      {children}
    </WizardDataContext.Provider>
  );
};
